import { parse as dateFNSParse } from 'date-fns/parse';
import { parseISO as dateFNSParseISO } from 'date-fns/parseISO';
import { format as dateFNSFormat } from 'date-fns/format';
import { formatDistanceStrict as dateFNSFormatDistance } from 'date-fns/formatDistanceStrict';
import { getDaysInMonth as dateFNSGetDaysInMonth } from 'date-fns/getDaysInMonth';
// region Min/Max
import { min as dateFNSMin } from 'date-fns/min';
import { max as dateFNSMax } from 'date-fns/max';
// endregion
// region add
import { addMinutes as dateFNSAddMinutes } from 'date-fns/addMinutes';
import { addHours as dateFNSAddHours } from 'date-fns/addHours';
import { addDays as dateFNSAddDays } from 'date-fns/addDays';
import { addWeeks as dateFNSAddWeeks } from 'date-fns/addWeeks';
import { addMonths as dateFNSAddMonths } from 'date-fns/addMonths';
import { addYears as dateFNSAddYears } from 'date-fns/addYears';
import { addMilliseconds as dateFNSAddMilliseconds } from 'date-fns/addMilliseconds';
// endregion
// region sub
import { subMinutes as dateFNSSubMinutes } from 'date-fns/subMinutes';
import { subHours as dateFNSSubHours } from 'date-fns/subHours';
import { subDays as dateFNSSubDays } from 'date-fns/subDays';
import { subWeeks as dateFNSSubWeeks } from 'date-fns/subWeeks';
import { subMonths as dateFNSSubMonths } from 'date-fns/subMonths';
import { subYears as dateFNSSubYears } from 'date-fns/subYears';
import { subMilliseconds as dateFNSSubMilliseconds } from 'date-fns/subMilliseconds';
// endregion
// region Start Of
import { startOfMinute as dateFNSStartOfMinute } from 'date-fns/startOfMinute';
import { startOfHour as dateFNSStartOfHour } from 'date-fns/startOfHour';
import { startOfDay as dateFNSStartOfDay } from 'date-fns/startOfDay';
import { startOfWeek as dateFNSStartOfWeek } from 'date-fns/startOfWeek';
import { startOfMonth as dateFNSStartOfMonth } from 'date-fns/startOfMonth';
import { startOfYear as dateFNSStartOfYear } from 'date-fns/startOfYear';
// endregion
// region End Of
import { endOfMinute as dateFNSEndOfMinute } from 'date-fns/endOfMinute';
import { endOfHour as dateFNSEndOfHour } from 'date-fns/endOfHour';
import { endOfDay as dateFNSEndOfDay } from 'date-fns/endOfDay';
import { endOfWeek as dateFNSEndOfWeek } from 'date-fns/endOfWeek';
import { endOfMonth as dateFNSEndOfMonth } from 'date-fns/endOfMonth';
import { endOfYear as dateFNSEndOfYear } from 'date-fns/endOfYear';
// endregion
// region diff
import { differenceInMinutes as dateFNSDifferenceInMinutes } from 'date-fns/differenceInMinutes';
import { differenceInHours as dateFNSDifferenceInHours } from 'date-fns/differenceInHours';
import { differenceInDays as dateFNSDifferenceInDays } from 'date-fns/differenceInDays';
import { differenceInWeeks as dateFNSDifferenceInWeeks } from 'date-fns/differenceInWeeks';
import { differenceInMonths as dateFNSDifferenceInMonths } from 'date-fns/differenceInMonths';
import { differenceInYears as dateFNSDifferenceInYears } from 'date-fns/differenceInYears';
import { differenceInMilliseconds as dateFNSDifferenceInMilliseconds } from 'date-fns/differenceInMilliseconds';
// endregion
// region IS
import { isAfter as dateFNSIsAfter } from 'date-fns/isAfter';
import { isFuture as dateFNSIsFuture } from 'date-fns/isFuture';
import { isBefore as dateFNSIsBefore } from 'date-fns/isBefore';
import { isValid as dateFNSIsValid } from 'date-fns/isValid';
// endregion
// region IS SAME
import { isSameMinute as dateFNSIsSameMinute } from 'date-fns/isSameMinute';
import { isSameHour as dateFNSIsSameHour } from 'date-fns/isSameHour';
import { isSameDay as dateFNSIsSameDay } from 'date-fns/isSameDay';
import { isSameWeek as dateFNSIsSameWeek } from 'date-fns/isSameWeek';
import { isSameMonth as dateFNSIsSameMonth } from 'date-fns/isSameMonth';
import { isSameYear as dateFNSIsSameYear } from 'date-fns/isSameYear';
// endregion
import { UTCDate } from '@date-fns/utc';
import { compareDesc as dateFNSCompareDesc } from 'date-fns/compareDesc';
import { isWednesday as dateFNSIsWednesday } from 'date-fns/isWednesday';
import { previousWednesday as dateFNSPreviousWednesday } from 'date-fns/previousWednesday';

import { isEqual as dateFNSIsEqual } from 'date-fns/isEqual';
import { areIntervalsOverlapping as dateFNSAreIntervalsOverlapping } from 'date-fns/areIntervalsOverlapping';
import {
  formatDuration as dateFNSFormatDuration,
  FormatDurationOptions,
} from 'date-fns/formatDuration';
import { intervalToDuration as dateFNSIntervalToDuration } from 'date-fns/intervalToDuration';
import { Duration, Interval } from 'date-fns/types';
import { DateTimeFormat, DateTimeFormatParse, DateTimeFormatTZ } from './date.constants';

export type DateInput = number | string | Date | null;
export type DurationUnit = 'minute' | 'hour' | 'day' | 'week' | 'month' | 'year' | 'millisecond';

export const parseDate = (date: DateInput, format?: DateTimeFormatParse): Date => {
  if (format && typeof date === 'string') {
    return dateFNSParse(date, format, new Date());
  }
  if (typeof date === 'number') {
    return new Date(date);
  }
  if (typeof date === 'string') {
    return dateFNSParseISO(date);
  }
  if (date instanceof Date) {
    return date;
  }
  return new Date();
};

export const formatDateTime = (
  date: Date,
  format: DateTimeFormat = DateTimeFormat.Default,
): string => {
  return dateFNSFormat(date, format);
};

export const formatDuration = (duration: Duration, options?: FormatDurationOptions): string => {
  return dateFNSFormatDuration(duration, options);
};

export const intervalToDuration = (interval: Interval<Date>): Duration => {
  return dateFNSIntervalToDuration(interval);
};

export const getUserTimezone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone;
export const getUserLocale = (): string => Intl.DateTimeFormat().resolvedOptions().locale;

const getDateFormatParts = (params: { date: Date; locale?: string; timeZone: string }) => {
  const parts = new Intl.DateTimeFormat(params.locale, {
    timeZone: params.timeZone,
    month: '2-digit',
    year: 'numeric',
    day: '2-digit',
    hour: '2-digit',
    hour12: false,
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  }).formatToParts(params.date);

  const partsExtra = new Intl.DateTimeFormat(params.locale, {
    timeZone: params.timeZone,
    year: '2-digit',
    timeZoneName: 'longOffset',
  }).formatToParts(params.date);

  const year = parts.find((part) => part.type === 'year')?.value || null;
  const month = parts.find((part) => part.type === 'month')?.value || null;
  const day = parts.find((part) => part.type === 'day')?.value || null;
  const hour = parts.find((part) => part.type === 'hour')?.value || null;
  const minute = parts.find((part) => part.type === 'minute')?.value || null;
  const second = parts.find((part) => part.type === 'second')?.value || null;
  const timeZoneName = parts.find((part) => part.type === 'timeZoneName')?.value || null;

  const timeZoneOffset = partsExtra.find((part) => part.type === 'timeZoneName')?.value || null;
  const year2Digits = partsExtra.find((part) => part.type === 'year')?.value || null;

  return { year, year2Digits, month, day, hour, minute, second, timeZoneName, timeZoneOffset };
};

export const formatDateTimeTZ = (
  date: Date,
  timeZone: string,
  format: DateTimeFormatTZ,
): string => {
  const locale = getUserLocale();
  const { year, year2Digits, month, day, hour, minute, second, timeZoneName, timeZoneOffset } =
    getDateFormatParts({
      date,
      timeZone,
      locale,
    });

  if (format === DateTimeFormatTZ.TimeWithShortTimeZone) {
    return `${hour}:${minute}:${second} ${timeZoneName}`;
  }

  if (format === DateTimeFormatTZ.Time24h) {
    return `${hour}:${minute}:${second}`;
  }

  if (format === DateTimeFormatTZ.Date) {
    return `${month}-${day}-${year2Digits}`;
  }

  if (format === DateTimeFormatTZ.DateWithSlash) {
    return `${year}/${month}/${day}`;
  }

  if (format === DateTimeFormatTZ.DateTimeWithShortTimeZone) {
    return `${year}/${month}/${day} ${hour}:${minute}:${second} ${timeZoneName}`;
  }

  if (format === DateTimeFormatTZ.ShortTimeZone) {
    return timeZoneName || 'TimeZone N/A';
  }

  if (format === DateTimeFormatTZ.DateTimeSlashed12hWithShortTimeZone) {
    const dateTimeFormatted = formatDateTime(date, DateTimeFormat.DateTimeSlashed12h);
    return `${dateTimeFormatted} ${timeZoneName || 'TimeZone N/A'}`;
  }

  if (format === DateTimeFormatTZ.TimeZoneOffset) {
    return timeZoneOffset?.split('GMT')?.[1] || 'Offset N/A';
  }

  if (format === DateTimeFormatTZ.ShortTimeZoneWithOffset) {
    return `${timeZoneName} (${timeZoneOffset})`;
  }

  return 'Unknown format';
};

export const getUserTimezoneFullInfo = () => {
  const timezoneName = getUserTimezone();
  const timezoneShortNameWithOffset = formatDateTimeTZ(
    new Date(),
    getUserTimezone(),
    DateTimeFormatTZ.ShortTimeZoneWithOffset,
  );
  return `${timezoneShortNameWithOffset} ${timezoneName}`;
};

// region - Date/Time add/sub
const mapAdd: Record<DurationUnit, (date: Date, duration: number) => Date> = {
  millisecond: dateFNSAddMilliseconds,
  minute: dateFNSAddMinutes,
  hour: dateFNSAddHours,
  day: dateFNSAddDays,
  week: dateFNSAddWeeks,
  month: dateFNSAddMonths,
  year: dateFNSAddYears,
};

const mapSub: Record<DurationUnit, (date: Date, duration: number) => Date> = {
  millisecond: dateFNSSubMilliseconds,
  minute: dateFNSSubMinutes,
  hour: dateFNSSubHours,
  day: dateFNSSubDays,
  week: dateFNSSubWeeks,
  month: dateFNSSubMonths,
  year: dateFNSSubYears,
};

export const calcDateTime = (
  date: Date,
  action: 'add' | 'sub',
  duration: number,
  unit: DurationUnit,
) => {
  if (action === 'add') {
    return mapAdd[unit](date, duration);
  }
  return mapSub[unit](date, duration);
};
// endregion

// region - Date/Time difference
const mapDiff: Record<DurationUnit, (start: Date, end: Date) => number> = {
  millisecond: dateFNSDifferenceInMilliseconds,
  minute: dateFNSDifferenceInMinutes,
  hour: dateFNSDifferenceInHours,
  day: dateFNSDifferenceInDays,
  week: dateFNSDifferenceInWeeks,
  month: dateFNSDifferenceInMonths,
  year: dateFNSDifferenceInYears,
};

// Example: dateStart Jan 10, dateEnd Jan 15 => -5 days
// Example: dateStart Jan 15, dateEnd Jan 10 => 5 days
export const diffDateTime = (dateStart: Date, dateEnd: Date, unit: DurationUnit) => {
  return mapDiff[unit](dateStart, dateEnd);
};
// endregion

// region - Date/Time is same
const mapIsSame: Record<DurationUnit, (start: Date, end: Date) => boolean> = {
  millisecond: (start, end) => start.getTime() === end.getTime(),
  minute: dateFNSIsSameMinute,
  hour: dateFNSIsSameHour,
  day: dateFNSIsSameDay,
  week: dateFNSIsSameWeek,
  month: dateFNSIsSameMonth,
  year: dateFNSIsSameYear,
};

export const isSameDateTime = (
  dateStart: Date,
  dateEnd: Date,
  unit: DurationUnit = 'millisecond',
) => {
  return mapIsSame[unit](dateStart, dateEnd);
};
// endregion

// region - Start Of Date/Time
const mapStartOf: Record<DurationUnit, (start: Date) => Date> = {
  millisecond: () => {
    throw new Error('startOfDateTime:millisecond not implemented');
  },
  minute: dateFNSStartOfMinute,
  hour: dateFNSStartOfHour,
  day: dateFNSStartOfDay,
  week: dateFNSStartOfWeek,
  month: dateFNSStartOfMonth,
  year: dateFNSStartOfYear,
};

export const startOfDateTime = (dateStart: Date, unit: DurationUnit) => {
  return mapStartOf[unit](dateStart);
};
// endregion

// region - End Of Date/Time
const mapEndOf: Record<DurationUnit, (start: Date) => Date> = {
  millisecond: () => {
    throw new Error('endOfDateTime:millisecond not implemented');
  },
  minute: dateFNSEndOfMinute,
  hour: dateFNSEndOfHour,
  day: dateFNSEndOfDay,
  week: dateFNSEndOfWeek,
  month: dateFNSEndOfMonth,
  year: dateFNSEndOfYear,
};

export const endOfDateTime = (dateStart: Date, unit: DurationUnit): Date => {
  return mapEndOf[unit](dateStart);
};
// endregion

export const isAfterDateTime = (date: Date, compareWithDate: Date): boolean => {
  return dateFNSIsAfter(date, compareWithDate);
};

export const isBeforeDateTime = (date: Date, compareWithDate: Date): boolean => {
  return dateFNSIsBefore(date, compareWithDate);
};

export const isFuture = (date: Date): boolean => {
  return dateFNSIsFuture(date);
};

export const getDaysInMonth = (date: Date): number => {
  return dateFNSGetDaysInMonth(date);
};

export const durationDateTime = (duration: number, unit: DurationUnit): string => {
  // date-fns.formatDuration  does not work in proper way
  // it returns values like '1000 minutes' and so on.
  // so date-fns.formatDistance is exactly what we need
  // but it works only with two dates objects - > so we need convert duration into date objects
  const dateFrom = new Date();
  const dateTo = calcDateTime(dateFrom, 'add', duration, unit);
  return dateFNSFormatDistance(dateFrom, dateTo);
};

// @ts-ignore Poor date-fns type definition
export const isDateTimeValid: (date: unknown) => date is Date = dateFNSIsValid;

export const minDateTime = (dates: Date[]): Date | undefined => dateFNSMin(dates);
export const maxDateTime = (dates: Date[]): Date | undefined => dateFNSMax(dates);

export const toUtcDateTime = (date: Date): Date => new UTCDate(date);
export const toLocalDateTime = (utcDate: Date): Date => new Date(utcDate);

export const compareDesc = (dateLeft: string | number | Date, dateRight: string | number | Date) =>
  dateFNSCompareDesc(dateLeft, dateRight);

export const getIsWednesday = (date: Date): boolean => dateFNSIsWednesday(date);
export const getPreviousWednesday = (date: Date): Date => dateFNSPreviousWednesday(date);

// Function Overloading
/* eslint-disable no-redeclare */
export function mergeDateAndTime(date: Date, time: Date): Date;
export function mergeDateAndTime(date?: Date | null, time?: Date | null): Date | null;
export function mergeDateAndTime(date?: Date | null, time?: Date | null): Date | null {
  if (!date || !time) {
    return null;
  }

  const result = new Date(date);
  result.setHours(time.getHours(), time.getMinutes());

  return result;
}

export const areYearMonthDateEqual = (date1: Date, date2: Date): boolean => {
  return dateFNSIsEqual(
    new Date(date1.getFullYear(), date1.getMonth(), date1.getDate(), 0, 0, 0, 0),
    new Date(date2.getFullYear(), date2.getMonth(), date2.getDate(), 0, 0, 0, 0),
  );
};

export const formatToUTCDateString = (date: Date): string => {
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())).toISOString();
};

/**
 * Checks if two date intervals overlap.
 * By default, adjacent intervals that touch at boundaries are NOT considered overlapping.
 */
export const areIntervalsOverlapping = (
  intervalLeft: Interval<Date>,
  intervalRight: Interval<Date>,
  options?: { inclusive?: boolean },
): boolean => {
  return dateFNSAreIntervalsOverlapping(intervalLeft, intervalRight, options);
};
